<template>
  <div class="form" :class="nftLayout">
    <div class="form__content">
      <h1 class="extra-large-headline" data-cy="update-detail-heading">
        {{ $t("UpdateDetailForm.label.headline") }}
      </h1>
    </div>
    <form @submit.prevent>
      <div class="form__content-wrapper">
        <FormInputField
          type="text"
          name="givenName"
          :inputValue="form.givenName"
          :label="$t('FormInputField.label.givenName')"
          :placeholder="$t('FormInputField.placeholder.givenName')"
          :errors="store.getErrorByField('givenName')"
          v-model:givenName="form.givenName"
        />
        <FormInputField
          type="text"
          name="middleName"
          :inputValue="form.middleName"
          :label="$t('FormInputField.label.middleName')"
          :placeholder="$t('FormInputField.placeholder.middleName')"
          v-model:middleName="form.middleName"
        />
        <FormInputField
          name="surname"
          :label="$t('FormInputField.label.lastName')"
          :placeholder="$t('FormInputField.placeholder.lastName')"
          type="text"
          :inputValue="form.surname"
          v-model:surname="form.surname"
        />

        <FormInputField
          class="form__addressLine1"
          name="addressLine1"
          type="text"
          iconType="MapPinCircle"
          :inputValue="form.addressLine1"
          :label="$t('FormInputField.label.addressLine1')"
          :placeholder="$t('FormInputField.placeholder.addressLine1')"
          v-model:addressLine1="form.addressLine1"
        />

        <FormInputField
          name="suburb"
          type="text"
          :inputValue="form.suburb"
          :placeholder="$t('FormInputField.placeholder.suburb')"
          :label="$t('FormInputField.label.suburb')"
          v-model:suburb="form.suburb"
        />

        <FormSelect
          v-if="showFieldsOnUSIdentity"
          type="text"
          name="usStates"
          :label="$t('FormSelect.label.usStates')"
          :placeholder="$t('FormInputField.placeholder.state')"
          :items="usStateList"
          @dropdownValueUpdate="updateDropdown"
        />

        <FormInputField
          v-else
          v-model:state="form.state"
          name="state"
          type="text"
          :inputValue="form.state"
          :label="$t('FormInputField.label.state')"
          :placeholder="$t('FormInputField.placeholder.state')"
        />

        <FormInputField
          v-model:postcode="form.postcode"
          type="text"
          name="postcode"
          :label="$t('FormInputField.label.postcode')"
          :placeholder="$t('FormInputField.placeholder.postcode')"
          :inputValue="form.postcode"
        />

        <AutocompleteSelect
          hasIcon
          v-model:autocomplete="form.country"
          :type="'text'"
          :items="countryList"
          :label="$t('Autocomplete.label.countryResidence')"
          :placeholder="$t('MobileAutocompleteSelect.placeholder.phoneCountry')"
          :isLoading="isFetchingData"
        />
      </div>

      <ButtonBase
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
        data-cy="button-update"
      >
        {{ $t("ButtonBase.label.submitMyDetails") }}
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useUpdateDetailFormStore } from "@/stores/forms/updateDetailFormStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import useApiHelpers from "@/composables/api/helpers";
import useSettingsEndpoints from "@/composables/api/useSettingsEndpoints";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";
import useCustomerActions from "@/composables/api/useCustomerActions";

import FormInputField from "@/components/ui/inputs/FormInputField.vue";
import FormSelect from "@/components/ui/selects/FormSelect.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";

const globalStore = useGlobalStore();
const verificationStore = useVerificationStore();
const store = useUpdateDetailFormStore();
const identityQueueStore = useIdentityQueueStore();
const orderStore = useOrderFormStore();

const { postRequest } = useApiHelpers();
const { getRestrictedUSStatesSettings } = useSettingsEndpoints();
const router = useRouter();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { fetchCountries } = useGlobalCountriesActions();
const { fetchCustomerDetails, fillForm, fetchCustomerId } =
  useCustomerActions();
const route = useRoute();

const countryList = ref([]);
const stateList = ref([]);
const usStateList = ref([]);
const disabledStatesList = ref([]);
const showUSStatesField = ref(false);
const form = ref({
  givenName: store.form.givenName.value || "",
  middleName: store.form.middleName.value || "",
  surname: store.form.surname.value || "",
  addressLine1: store.form.addressLine1.value || "",
  suburb: store.form.suburb.value || "",
  state: store.form.state.value || "",
  postcode: store.form.postcode.value || "",
  country: verificationStore.form.country.value || "",
});
const isFetchingData = ref(true);

const nftLayout = computed(() => {
  return orderStore.isNFTMode ? "nft-layout" : "";
});

const getCountryRelatedData = async () => {
  const {
    data: { acceptedStates, restrictedStates },
  } = await getRestrictedUSStatesSettings();

  const restrictedStateCodes = Object.keys(restrictedStates);

  stateList.value = {
    ...acceptedStates,
    ...restrictedStates,
  };

  disabledStatesList.value = restrictedStateCodes;
};

const showFieldsOnUSIdentity = computed(() => {
  const usNames = ["us", "united states"];
  return usNames.some(
    (country) => country === form.value.country.toLowerCase()
  );
});

const createUSStateItemList = computed(() => {
  return getUSStateList();
});

const getUSStateList = () => {
  const states = Object.entries(stateList.value);
  if (states.length === 0) return [];
  return states.map(([stateCode, stateName]) => {
    return {
      name: stateName,
      value: stateCode,
      isDisabled: disabledStatesList.value.includes(stateCode),
    };
  });
};

const updateDropdown = (dropdownData) => {
  form.value.state = Object.values(dropdownData)[0]?.value;
};

const handleFormSuccess = async () => {
  identityQueueStore.updateToNextRoute();
  identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
    router.push(nextRoute);
  });
};

const handleFormSubmit = async () => {
  const { country, ...rest } = form.value;
  const countryCode = globalStore.getCountryCode(country);
  globalStore.setIsFormLoading(true);

  const payload = {
    ...form.value,
    personalDetailsUnchanged: false,
    country: countryCode,
  };

  postRequest("/customer/update-details", payload, handleFormSuccess);
};

onMounted(async () => {
  await getCountryRelatedData();
  const formattedCountries = await fetchCountries();

  countryList.value = formattedCountries;

  const personalDetails = await fetchCustomerDetails();

  if (personalDetails.country === "US") {
    usStateList.value = getUSStateList();
    showUSStatesField.value = true;
    personalDetails.country = "United States";
  }

  await fillForm(personalDetails, form, store);

  isFetchingData.value = false;
});

watchAndUpdateStore(form, store, () => {});
</script>

<style lang="scss" scoped>
.form {
  &__content-wrapper {
    display: flex;
    flex-wrap: wrap;

    .base-input,
    .occupation-field,
    .base-form-select,
    .autocomplete-select,
    .base-field {
      flex-grow: 1;
      padding-inline: 0.5rem;

      @include breakpoint($min-l) {
        &:not(.form__addressLine1) {
          width: 50%;
        }
      }
    }
  }
}

.nft-layout {
  .base-input,
  .occupation-field,
  .base-form-select,
  .autocomplete-select,
  .base-field {
    &:not(.form__addressLine1) {
      width: 50%;
    }
  }
}
</style>
